import { Img, Input, Text } from "components";
import globalRequest from "global-modules/globalRequest";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeChatbox, changeChatObject } from "redux/reducers/chatbox";
import { changeLoader } from "redux/reducers/loader";
import { changeSnackbar } from "redux/reducers/snackbar";
import { currentUserData } from "redux/reducers/token";
import socket from "socket";
import { API } from "studentEnum";

import { formatChatTime } from "../../Helpers/Functions";

const ChatBox = ({ closeChat }) => {
  const dispatch = useDispatch();

  const currentUser = useSelector(currentUserData);

  const [chatRoomData, setchatRoomData] = useState([]);
  const [chatRoomDataAll, setchatRoomDataAll] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const getchatrooms = async () => {
    if (isFetching) return; // Prevent duplicate calls
    setIsFetching(true);

    dispatch(changeLoader(true));
    let chatRoomData = [];
    let url = API.CHAT.GET_CHAT_ROOMS;

    try {
      const response = await globalRequest("get", url, {}, {}, true, true);

      if (response?.status === "SUCCESS") {
        chatRoomData = response?.data;
        setchatRoomData(chatRoomData);
        setchatRoomDataAll(chatRoomData);
        dispatch(changeLoader(false));
      } else {
        dispatch(changeLoader(false));
        dispatch(
          changeSnackbar({
            isOpen: true,
            message: response?.message,
            state: "error",
          })
        );
      }
    } catch (error) {
      console.error("❌ Error fetching chat rooms:", error);
    } finally {
      setIsFetching(false);
      dispatch(changeLoader(false));
    }
  };

  const closeChats = async () => {
    dispatch(changeChatbox(false));
  };

  const chatMessage = (_, sender) => {
    dispatch(
      changeChatObject({
        tutorId: sender.id,
        isSingle: true,
        tutor: sender,
      })
    );
  };

  const handleKeyPress = (event) => {
    const filteredData = chatRoomDataAll?.filter(
      (item) =>
        item?.sender?.firstName?.toLowerCase().includes(event.toLowerCase()) ||
        item?.sender?.lastName?.toLowerCase().includes(event.toLowerCase())
    );

    setchatRoomData(filteredData);
  };

  function truncateMessage(message, maxLength) {
    if (message.length > maxLength) {
      return message.substring(0, maxLength) + " ...";
    } else {
      return message;
    }
  }

  /*
   * This useEffect will run when the component is mounted
   * It will listen for the "recieveMessage" event from the socket
   * And call the getchatrooms function to update the chat rooms
   */
  useEffect(() => {
    let timeoutId = null;

    const handleReceiveMessage = () => {
      console.log("📩 New message received, updating chat rooms...");

      // Debounce multiple calls
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        getchatrooms();
      }, 500); // Delay to avoid excessive calls
    };

    getchatrooms(); // Fetch initially
    socket.on("recieveMessage", handleReceiveMessage);
    return () => {
      socket.off("recieveMessage", handleReceiveMessage);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <div
        className={`flex flex-col font-proximanova items-end justify-end mx-auto w-auto fixed top-auto bottom-7 sm:bottom-3 right-7 sm:right-3 h-auto z-[99]`}
      >
        <div className="bg-white_A700 flex flex-col items-center justify-start pb-[0px] h-auto w-full max-w-[350px] min-w-[350px] sm:max-w-[290px] drop-shadow-md">
          <div className="bg-gray_50 flex flex-col items-center justify-end p-[15px] w-full">
            <div className="flex flex-row items-center justify-between w-[97%] md:w-full">
              <Text className="font-semibold text-black_900" variant="body1">
                Chat
              </Text>
              <Img
                onClick={() => closeChats(false)}
                src="images/img_close_black_900_24x24.svg"
                className="h-6 w-6 cursor-pointer"
                alt="close"
              />
            </div>
          </div>
          <div className="chatSearchBox relative w-full">
            <Input
              type="text"
              placeholder={
                currentUser.roleType === "student"
                  ? "Search by tutor name"
                  : "Search by student name"
              }
              className="h-[48px] mb-0"
              name="searchTerm"
              onChange={(e) => handleKeyPress(e.target.value)}
            />
            <Img
              src="images/img_search.svg"
              className="h-6 w-6 cursor-pointer absolute top-[10px] right-[15px] searchIcon"
              alt="Search"
            />
          </div>

          <div className="flex flex-col items-center justify-start pt-[17px] w-full chat-custome-height overflow-auto">
            {chatRoomData?.length > 0 ? (
              chatRoomData.map((chat) => {
                const isUnread = chat.unread_message_count > 0;
                return (
                  <div
                    key={`chatRoomData-${chat.id}`}
                    className="flex flex-row items-start justify-start w-full px-4 py-3 hover:bg-gray_50 cursor-pointer"
                    onClick={() => {
                      //setCheck(!check);
                      chatMessage(true, chat.sender);
                      closeChats(false);
                    }}
                  >
                    {/* Profile Image */}
                    <Img
                      type="profile"
                      src={
                        chat.sender.profile_image ||
                        "images/img_user_black_900.svg"
                      }
                      className="h-10 md:h-auto object-cover w-10"
                      alt="rectangleNineteen"
                    />

                    {/* Chat Info */}
                    <div className="flex flex-col items-start justify-start ml-3 flex-grow">
                      {/* Name */}
                      <Text
                        className="font-semibold text-black_900"
                        variant="body2"
                      >
                        {chat.sender.firstName} {chat.sender.lastName.charAt(0)}
                      </Text>

                      {/* Last Message */}
                      <Text
                        className="font-normal mt-1 text-black_900 overflow-hidden whitespace-nowrap overflow-ellipsis"
                        variant="body3"
                      >
                        {truncateMessage(
                          chat.last_message_in_chat_room || "No messages",
                          30
                        )}
                      </Text>
                    </div>

                    {/* Time & Unread Messages */}
                    <div className="flex flex-col items-end justify-center">
                      {/* Time (green if unread) */}
                      <Text
                        className={`font-semibold text-right ${
                          isUnread ? "text-green-600" : "text-gray-600"
                        }`}
                        variant="body3"
                      >
                        {formatChatTime(chat.last_message_timestamp)}
                      </Text>

                      {/* Unread Count (only if unread) */}
                      {isUnread && (
                        <div className="bg-green-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                          {chat.unread_message_count}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p>Not Found</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export { ChatBox };
